// FAZ-10 / K-18/K-20/K-24 — componentJson (BE kaynak-hakikat) ⇄ Puck Data eşlemesi.
//
// BE sözleşmesi: `{ "root": {}, "content": [ {type, props} ] }`. Slot alanları
// props içinde nested `{type, props}` dizisidir. Puck her düğüme benzersiz bir
// `id` ister; kalıcılaştırmada bu id'ler STRIP edilir (deterministik depolama →
// temiz versiyon diff'i, K-24), yüklemede yeniden ÜRETİLİR. Bu, sayfa-yapıcı
// mapping'iyle (blocks/mapping.ts) aynı desendir; burada nested slot'lara özyineler.
//
// `@puckeditor/core` import ETMEZ — saf ve birim-test edilebilir kalır.

import { nanoid } from 'nanoid';

/** Puck içerik öğesinin dokunduğumuz minimal şekli. */
export interface PuckStudioItem {
  type: string;
  props: Record<string, unknown>;
}

export interface PuckStudioData {
  root: Record<string, unknown>;
  content: PuckStudioItem[];
}

/** Puck'ın her prop'a enjekte ettiği, kalıcılaştırılmaması gereken anahtarlar. */
const PUCK_INTERNAL_KEYS = ['id', 'editMode', 'puck'] as const;

function isInternalKey(key: string): boolean {
  return (PUCK_INTERNAL_KEYS as readonly string[]).includes(key);
}

/** Bir prop değeri slot mu? (her elemanı `{type, props}` olan dizi; boş dizi de slot sayılır). */
function isSlotArray(value: unknown): value is PuckStudioItem[] {
  return (
    Array.isArray(value) &&
    value.every(
      (el) =>
        !!el &&
        typeof el === 'object' &&
        !Array.isArray(el) &&
        typeof (el as { type?: unknown }).type === 'string' &&
        !!(el as { props?: unknown }).props &&
        typeof (el as { props?: unknown }).props === 'object',
    )
  );
}

/** Kaydet: Puck id'lerini özyineli olarak strip et (slot'lar dahil). */
function stripItem(item: PuckStudioItem): PuckStudioItem {
  const props: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(item.props ?? {})) {
    if (isInternalKey(key)) continue;
    props[key] = isSlotArray(value) ? value.map(stripItem) : value;
  }
  return { type: item.type, props };
}

/**
 * Kaydet: Puck Data → BE componentJson (canonical). `root` daima `{}`, id'ler
 * strip edilir. BE store-time recursive validator + canonical re-serialize eder;
 * bu çıktı yalnız geçerli yapı sağlar.
 */
export function puckDataToComponentJson(data: PuckStudioData): string {
  const content = (data.content ?? []).map(stripItem);
  return JSON.stringify({ root: {}, content });
}

/** Yükle: bir düğüme (ve slot'larına) taze, benzersiz Puck id'leri ver. */
function withIds(item: PuckStudioItem): PuckStudioItem {
  const props: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(item.props ?? {})) {
    if (isInternalKey(key)) continue;
    props[key] = isSlotArray(value) ? value.map(withIds) : value;
  }
  // Puck benzersiz + stabil id ister; charset nanoid = [A-Za-z0-9_-].
  props.id = `${item.type}-${nanoid(8)}`;
  return { type: item.type, props };
}

/** Ham BE JSON'undan yalnız `{type, props}` düğümlerini süz. */
function extractContent(raw: unknown): PuckStudioItem[] {
  if (!raw || typeof raw !== 'object') return [];
  const content = (raw as { content?: unknown }).content;
  if (!Array.isArray(content)) return [];
  const out: PuckStudioItem[] = [];
  for (const el of content) {
    if (!el || typeof el !== 'object' || Array.isArray(el)) continue;
    const type = (el as { type?: unknown }).type;
    if (typeof type !== 'string') continue;
    const props = (el as { props?: unknown }).props;
    out.push({ type, props: props && typeof props === 'object' ? (props as Record<string, unknown>) : {} });
  }
  return out;
}

/**
 * Yükle: BE componentJson → Puck Data. Geçersiz/boş JSON → boş canvas. Her düğüme
 * taze id verilir (Puck benzersizliği garantisi).
 */
export function componentJsonToPuckData(json: string | null | undefined): PuckStudioData {
  if (!json || !json.trim()) return { root: {}, content: [] };
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { root: {}, content: [] };
  }
  return { root: {}, content: extractContent(raw).map(withIds) };
}

/**
 * K-20 legacy köprüsü: componentJson'u OLMAYAN (yalnız templateHtml) bir bileşen
 * editörde açılınca, mevcut şablon tek bir salt-okunur `legacyHtml` bloğu olarak
 * canvas'a gelir. Kullanıcı yeni bloklarla yeniden kompoze edebilir.
 */
export function legacyTemplateToPuckData(templateHtml: string | null | undefined): PuckStudioData {
  const html = templateHtml ?? '';
  if (!html.trim()) return { root: {}, content: [] };
  return { root: {}, content: [withIds({ type: 'legacyHtml', props: { html } })] };
}

/** K-24 versiyon anlık görüntüsü — componentJson varsa pretty JSON, yoksa ham metin. */
export function prettyPrintJson(json: string | null | undefined): string {
  if (!json || !json.trim()) return '';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
